import { uniq } from "lodash";
import { IVisualizationRange } from "~/interfaces/visualizationInterfaces";
import { RangeMode } from "../../../formability/visualizationBuilder/enums/rangeMode";

export default class RangesUtils {
  ranges: IVisualizationRange[];

  constructor(ranges) {
    this.ranges = ranges;
  }

  index(range): number {
    return this.ranges.indexOf(range);
  }

  baseAdder(range) {
    return range.parent.decimalPlaces ? parseFloat((1 / (10 ** range.parent.decimalPlaces)).toFixed(range.parent.decimalPlaces)) : 1;
  }

  prevRange(range) {
    return this.ranges[this.index(range) - 1];
  }

  fromValue(range): number {
    if (this.index(range) === 0) {
      return range.value !== null ? parseFloat(range.value) : null;
    }

    const prevVal = this.toValue(this.prevRange(range));

    if (this.index(range) === 1) {
      return prevVal !== null ? parseFloat(prevVal.toString()) : null;
    }

    return prevVal !== null ? parseFloat(prevVal.toString()) + this.baseAdder(range) : null;
  }

  toValue(range): number {
    if (range.inclusive) {
      return range.value !== null ? parseFloat(range.value) : null;
    }

    return parseFloat(range.value) - this.baseAdder(range);
  }

  rangeString(range) {
    let minVal: string = this.fromValue(range) !== null ? this.fromValue(range).toFixed(range.parent.decimalPlaces) : null;
    let maxVal: string = this.toValue(range) !== null ? this.toValue(range).toFixed(range.parent.decimalPlaces) : null;

    if (this.prevRange(range).mode === RangeMode.min) {
      minVal = 'Minimum Value';
    }

    if (range.mode === RangeMode.max) {
      maxVal = 'Maximum Value';
    }

    const sep = range.repeat ? '...' : '-';

    return `${minVal} ${sep} ${maxVal}`;
  }

  previousRangeSize(range) {
    if (!this.toValue(this.prevRange(range)) || !this.fromValue(this.prevRange(range))) {
      return 'unknown';
    }

    return parseFloat((this.toValue(this.prevRange(range)) - this.fromValue(this.prevRange(range)) + this.baseAdder(range)).toFixed(range.parent.decimalPlaces));
  }

  rangeBuckets(range) {
    if (!range.repeat) {
      return [];
    }

    const buckets = [];
    let prevValue = parseFloat(this.fromValue(range).toFixed(range.parent.decimalPlaces));
    let endValue = this.toValue(range) ? parseFloat(this.toValue(range).toFixed(range.parent.decimalPlaces)) : null;

    if (!prevValue && prevValue !== 0) {
      return [];
    }

    const step = this.previousRangeSize(range);
    if (step === 'unknown') {
      const endRange = this.toValue(range) ? endValue : 'Maximum Value';

      buckets.push({
        range: `${prevValue.toFixed(range.parent.decimalPlaces)} ... ${endRange}`,
        size: 'unknown',
        start: prevValue,
        end: 'Maximum Value'
      });

      return buckets;
    }
    
    const numOfBuckets = (this.toValue(range) - this.fromValue(range) + this.baseAdder(range)) / step;

    if (this.prevRange(this.prevRange(range)).mode === RangeMode.min) {
      return [{range: `${this.rangeString(range)}`, size: step, start: prevValue, end: prevValue + step - this.baseAdder(range)}];
    }

    for (let j = 0; j < numOfBuckets - 1; j++) {
      buckets.push({range: `${prevValue.toFixed(range.parent.decimalPlaces)} - ${prevValue + step - this.baseAdder(range)}`, size: step, start: prevValue, end: prevValue + step - this.baseAdder(range)});
      prevValue = prevValue + step;
    }

    let max: number | string = 'Maximum Value';
    let sep = '...';
    let size = this.previousRangeSize(range);

    if (range.mode === RangeMode.number) {
      max = parseFloat(this.toValue(range).toString()).toFixed(range.parent.decimalPlaces);
      sep = '-';
      size = parseFloat((this.toValue(range) - prevValue + this.baseAdder(range)).toFixed(range.parent.decimalPlaces))
    }

    buckets.push({
      range: `${prevValue.toFixed(range.parent.decimalPlaces)} ${sep} ${max}`,
      size,
      start: prevValue,
      end: max
    });

    return buckets;
  }

  maxRowBuckets(liveData) {
    if (!Array.isArray(liveData)) { 
      return uniq(liveData["row_agg_c"]["buckets"].map(bucket => `${bucket.key}`));
    };

    return uniq(this.ranges[0].parent.parent.rowDataPointsArray.find(point => point.client_id === this.ranges[0].parent.client_id).liveRows.flat());
  }

  maxColBuckets(liveData) {
    if (!Array.isArray(liveData)) {
      return uniq(liveData["row_agg_c"]["buckets"].map(b => b.col_agg_c.buckets).flat().map(b => b.key));
    }

    return uniq(this.ranges[0].parent.parent.columnDataPointsArray.find(point => point.client_id === this.ranges[0].parent.client_id).liveCols.flat());
  }
}